import { Button, IconButton } from '@ads/components-react';
import { ArrowRightIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import type { ExamItemAnswerTextCorrectionGridItemRepositoryResponse } from '@/shared/api/generated/types';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import { AiCorrectionPanel } from './parts/AiCorrectionPanel';
import { CommentField } from './parts/CommentField';
import { CompletenessField } from './parts/CompletenessField';
import { QuestionSection } from './parts/QuestionSection';
import { ReferenceSection } from './parts/ReferenceSection';
import { SkeletonContent } from './parts/SkeletonContent';
import { StudentAnswer } from './parts/StudentAnswer';
import { useCorrectionModal } from './useCorrectionModal';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface CorrectionModalProps {
  /** ID da publicação do exame */
  readonly examPublishId: number | null;
  /** ID do item de questão aberto para correção */
  readonly examItemId: number | null;
  /** Item da grade com os dados da questão (label, etc.) — usado como fallback para o enunciado */
  readonly questionItem?: ExamItemAnswerTextCorrectionGridItemRepositoryResponse | null;
  /** ID da resposta do aluno a exibir inicialmente */
  readonly initialAnswerId?: number | null;
  /**
   * Posição de `initialAnswerId` na fila de respostas da questão (1-based, contando
   * todas as páginas da sub-tabela). É o cursor de onde "Próxima" continua.
   */
  readonly initialPosition?: number;
  /** Callback ao fechar o modal */
  readonly onClose: () => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism CorrectionModal — modal de correção de resposta de aluno.
 *
 * A navegação é sequencial sobre a fila de respostas da questão — a mesma lista e ordem
 * da sub-tabela de alunos, incluindo as já corrigidas. O cursor é a posição
 * (`initialPosition`), e cada avanço busca o ID daquela posição via fetchQuery, então
 * paginação e correções feitas por outra pessoa não deslocam o modal.
 *
 * Na última posição da fila não há "Próxima" e o botão primário passa a "Salvar e
 * Fechar", encerrando o modal depois do save.
 *
 * O estado vive em `useCorrectionModal`; aqui ficam apenas a composição das seções
 * (`parts/`) e o esqueleto do diálogo.
 *
 * @example
 * ```tsx
 * <CorrectionModal
 *   examPublishId={examPublishId}
 *   examItemId={selectedItem.id}
 *   questionItem={selectedItem}
 *   initialAnswerId={selectedAnswerId}
 *   onClose={() => setSelectedItem(null)}
 * />
 * ```
 */
export function CorrectionModal({
  examPublishId,
  examItemId,
  questionItem,
  initialAnswerId = null,
  initialPosition = 1,
  onClose,
}: CorrectionModalProps) {
  const { t } = useTranslation('examCorrection');

  const {
    details,
    isOpen,
    isContentLoading,
    isAudio,
    isMultilineText,
    label,
    student,
    author,
    value,
    subtypeLabel,
    modelAnswer,
    modelAnswerLabel,
    keyTerms,
    completeness,
    comment,
    isCommentLocked,
    setComment,
    handleCompletenessChange,
    aiStatus,
    aiResult,
    isAiAnalyzing,
    canAnalyze,
    handleAiCorrection,
    handleApplyAiSuggestion,
    hasNextAnswer,
    isNavigating,
    isSubmitting,
    handleNext,
    handleSaveAndContinue,
  } = useCorrectionModal({
    examPublishId,
    examItemId,
    questionItem,
    initialAnswerId,
    initialPosition,
    onClose,
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-lg py-xxs">
            <div className="flex flex-col gap-quark">
              <Dialog.Title className="text-ctx-content-title ts-heading-xs">
                {t('modal.title')}
              </Dialog.Title>
              {examItemId !== null && (
                <Dialog.Description className="text-ctx-content-placeholder ts-caption-xxs">
                  {t('modal.id', { id: examItemId })}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('modal.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="at-scrollbar flex-1 overflow-y-auto">
            {isContentLoading ? (
              <SkeletonContent />
            ) : !details ? (
              <div className="flex h-full items-center justify-center p-xl">
                <EmptyState
                  title={t('modal.noAnswer')}
                  description={t('modal.noAnswerDescription')}
                />
              </div>
            ) : (
              <div className="flex flex-col">
                <QuestionSection label={label} subtypeLabel={subtypeLabel} author={author} />

                <ReferenceSection
                  modelAnswer={modelAnswer}
                  modelAnswerLabel={modelAnswerLabel}
                  keyTerms={keyTerms}
                />

                <section className="px-lg py-xxs">
                  <h3 className="mb-xxs text-ctx-content-title ts-subtitle-sm">
                    {t('modal.sections.correction')}
                  </h3>

                  <div className="flex flex-col gap-mini">
                    <StudentAnswer student={student} value={value} isAudio={isAudio} />

                    {/* Corrigir com IA — apenas para questões dissertativas.
                        A ação de aplicar a sugestão fica no rodapé, não aqui. */}
                    {isMultilineText && (
                      <AiCorrectionPanel
                        status={aiStatus}
                        result={aiResult}
                        canAnalyze={canAnalyze}
                        onAnalyze={handleAiCorrection}
                      />
                    )}

                    <CompletenessField value={completeness} onChange={handleCompletenessChange} />

                    <CommentField
                      value={comment}
                      isLocked={isCommentLocked}
                      onChange={setComment}
                    />
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* Footer — uma única faixa: "sair" à esquerda, e à direita o que leva a
              correção adiante, em ênfase crescente: assistiva (fantasma) → navegação
              (contorno) → salvar (sólida). O grupo da direita não quebra linha; o
              aviso de estado é que cede espaço, porque é ele que varia de tamanho. */}
          <div className="flex flex-wrap items-center justify-between gap-micro border-t-hairline border-ctx-layout-border px-lg py-xxs">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md" disabled={isSubmitting || isNavigating}>
                {t('modal.actions.close')}
              </Button>
            </Dialog.Close>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-micro">
              {/* Por que "Salvar" está apagado. Fica ao lado do próprio botão, com o
                  giro que ecoa a análise em curso no corpo do modal. */}
              {isAiAnalyzing && (
                <p
                  role="status"
                  className="flex min-w-0 items-center gap-nano text-ctx-content-placeholder ts-caption-xxs"
                >
                  <span
                    aria-hidden="true"
                    className="size-micro shrink-0 animate-spin rounded-circular border-thin border-ctx-layout-border border-t-current"
                  />
                  <span className="truncate">{t('modal.ai.saveBlocked')}</span>
                </p>
              )}

              <div className="flex shrink-0 items-center gap-micro">
                {/* Aplicar a sugestão da IA mora no rodapé para não depender do scroll
                    do corpo: o resultado da análise é longo e a ação ficava fora de
                    vista. Só aparece quando há resultado, e segue disponível depois de
                    aplicada — reaplicar substitui o bloco de devolutiva em vez de
                    duplicá-lo. */}
                {aiStatus === 'success' && aiResult && (
                  <Button
                    hierarchy="tertiary"
                    size="md"
                    leadingIcon={SparklesIcon}
                    onClick={handleApplyAiSuggestion}
                    disabled={isSubmitting || isNavigating || !details}
                  >
                    {t('modal.actions.applyAiSuggestion')}
                  </Button>
                )}
                {/* "Próxima" percorre a fila até o fim, sem olhar o status da correção;
                    na última posição não há para onde ir e o botão sai de cena */}
                {hasNextAnswer && (
                  <Button
                    hierarchy="secondary"
                    size="md"
                    trailingIcon={ArrowRightIcon}
                    onClick={handleNext}
                    disabled={isSubmitting || isNavigating || !details}
                  >
                    {t('modal.actions.next')}
                  </Button>
                )}
                <Button
                  hierarchy="primary"
                  size="md"
                  onClick={handleSaveAndContinue}
                  isLoading={isSubmitting || isNavigating}
                  disabled={!details || isAiAnalyzing}
                >
                  {isSubmitting || isNavigating
                    ? t('modal.actions.saving')
                    : hasNextAnswer
                      ? t('modal.actions.saveAndContinue')
                      : t('modal.actions.saveAndClose')}
                </Button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
